/* Shadow & Specter About book: stable reader plus a single-instance community-collage scheduler. */
(() => {
  const shuffle = (items) => [...items].sort(() => Math.random() - 0.5);

  const initBook = (root) => {
    if (root.dataset.bookReady === 'true') return;

    const pages = [...root.querySelectorAll('[data-book-page]')];
    const leaf = root.querySelector('[data-book-leaf]');
    const previous = root.querySelector('[data-book-previous]');
    const next = root.querySelector('[data-book-next]');
    const current = root.querySelector('[data-book-current]');
    const total = root.querySelector('[data-book-total]');
    let pageIndex = 0;
    let isTransitioning = false;

    if (!pages.length || !leaf || !previous || !next || !current || !total) return;
    root.dataset.bookReady = 'true';
    total.textContent = String(pages.length);

    const sync = () => {
      const isFirst = pageIndex === 0;
      const isLast = pageIndex === pages.length - 1;
      pages.forEach((page, index) => {
        const active = index === pageIndex;
        page.classList.toggle('is-active', active);
        page.hidden = !active;
      });
      current.textContent = String(pageIndex + 1);
      previous.disabled = isFirst;
      previous.setAttribute('aria-label', isFirst ? 'Already at the cover' : 'Previous page');
      next.setAttribute('aria-label', isLast ? 'Return to the cover' : 'Next page');
    };

    const goTo = (targetIndex) => {
      if (isTransitioning || targetIndex === pageIndex || targetIndex < 0 || targetIndex >= pages.length) return;
      isTransitioning = true;
      leaf.classList.add('is-flipping');
      window.setTimeout(() => {
        pageIndex = targetIndex;
        sync();
        requestAnimationFrame(() => leaf.classList.remove('is-flipping'));
        isTransitioning = false;
      }, 170);
    };

    previous.addEventListener('click', () => goTo(pageIndex - 1));
    next.addEventListener('click', () => goTo(pageIndex === pages.length - 1 ? 0 : pageIndex + 1));
    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(pageIndex - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(pageIndex === pages.length - 1 ? 0 : pageIndex + 1);
      }
    });
    sync();
  };

  const initCollage = (root) => {
    if (root.dataset.collageReady === 'true') return;

    const source = root.querySelector('[data-collage-assets]');
    const cards = [...root.querySelectorAll('[data-collage-card]')];
    if (!source || !cards.length) return;

    let assets = [];
    try {
      assets = JSON.parse(source.textContent).filter((asset) => asset.id && asset.src);
    } catch (_) {
      return;
    }
    if (assets.length < cards.length) return;

    root.dataset.collageReady = 'true';
    const activeIds = new Set();
    let deck = shuffle(assets);

    const chooseAsset = () => {
      let candidate = deck.find((asset) => !activeIds.has(asset.id));
      if (!candidate) {
        deck = shuffle(assets);
        candidate = deck.find((asset) => !activeIds.has(asset.id));
      }
      deck = deck.filter((asset) => asset.id !== candidate.id);
      return candidate;
    };

    const assignAndRun = (card) => {
      const previousId = card.dataset.assetId;
      if (previousId) activeIds.delete(previousId);

      const asset = chooseAsset();
      if (!asset) return;
      const image = card.querySelector('[data-collage-image]');
      const sourceLabel = card.querySelector('[data-collage-source]');
      const title = card.querySelector('[data-collage-title]');
      if (!image || !sourceLabel || !title) return;

      activeIds.add(asset.id);
      card.dataset.assetId = asset.id;
      image.src = asset.src;
      sourceLabel.textContent = asset.source;
      title.textContent = asset.title;
      card.classList.remove('is-running');
      void card.offsetWidth;
      card.classList.add('is-ready', 'is-running');
    };

    cards.forEach((card) => {
      card.addEventListener('animationend', (event) => {
        if (event.animationName.startsWith('specter-lane-')) assignAndRun(card);
      });
      assignAndRun(card);
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        cards.forEach((card) => card.classList.toggle('is-paused', !entry.isIntersecting));
      }, { threshold: 0.08 });
      observer.observe(root);
    }
  };

  const initialize = (scope = document) => scope.querySelectorAll?.('[data-specter-about-book]').forEach((root) => {
    initBook(root);
    initCollage(root);
  });

  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
